import { DescriptionField } from "@dash/description-list";
import { useDialogRoute } from "@dash/dialog";
import { GoogleMap } from "@dash/google-map";
import { SectionHeader } from "@dash/page";
import { useTheme } from "@emotion/react";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom-v5-compat";

import { parseCustomerAddressToString } from "./CustomerTable";
import { EditCustomerAddressDialog } from "./EditCustomerAddressDialog";
import { PublicProfile } from "./PublicProfile";
import { CustomerData, ExtendedCustomerAddress } from "./useCustomerDetailsForm";

type Props = {
  customer: CustomerData;
  onAddressChange: (address: ExtendedCustomerAddress) => void;
  isLoading: boolean;
  isLoyalty?: boolean;
};

export const CustomerAddress: React.FC<Props> = ({ isLoading, onAddressChange, customer, isLoyalty }) => {
  const theme = useTheme();

  const [newAddress, setNewAddress] = useState<ExtendedCustomerAddress>(customer.address);
  const addressString = useMemo(() => parseCustomerAddressToString(customer.address), [customer.address]);

  useEffect(() => {
    if (!isEqual(customer.address, newAddress)) setNewAddress(customer.address);
  }, [customer.address]);

  const editAddressDialog = useDialogRoute(`/edit-address`, ({ onClose }) => (
    <EditCustomerAddressDialog
      onClose={onClose}
      customer={customer}
      onAddressChange={setNewAddress}
      onSave={() => {
        onAddressChange(newAddress);
      }}
      isLoading={isLoading}
    />
  ));

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1.5rem", width: "100%" }}>
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%", flexGrow: 0 }}>
        <SectionHeader isHr>Adres</SectionHeader>

        <button
          onClick={editAddressDialog.open}
          css={{ background: "none", border: "none", textAlign: "start", cursor: "pointer", padding: 0 }}
        >
          <DescriptionField
            key={`${newAddress.googlePlaceId ?? ""}-${newAddress.street ?? ""}-${newAddress.city ?? ""}-${
              newAddress.postalCode ?? ""
            }`}
            label="Adres"
          >
            {addressString}
          </DescriptionField>
        </button>

        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            color: theme.colors.primary,
            textDecoration: "underline",
          }}
        >
          {newAddress.googlePlaceId ? (
            <a
              href={`https://www.google.com/maps/place/?q=place_id:${customer.address.googlePlaceId}`}
              rel="noreferrer"
              target="_blank"
            >
              Zobacz na mapie
            </a>
          ) : (
            <Link to={`/crm/customers/new-map?customerId=${customer.uuid}`}>Zobacz na mapie</Link>
          )}
        </div>

        {customer.address.latitude && customer.address.longitude && (
          <GoogleMap latLng={{ latitude: customer.address.latitude, longitude: customer.address.longitude }} />
        )}

        {editAddressDialog.element}
      </div>

      {!isLoyalty && <PublicProfile customer={customer} onAddressChange={onAddressChange} />}
    </div>
  );
};
